import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, Loader2, PlugZap, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { apiError, useAuth } from "../../lib/auth";
import { NeonButton } from "../common";
import { PLATFORMS } from "./catalog";
import { CodeBlock } from "./CodeBlock";

const HEALTH_C = { healthy: "#CCFF00", stale: "#FFB800", waiting: "#F87171" };

const Step = ({ n, title, children, testid }) => (
  <div className="border-l-2 border-brand/50 pl-5 pb-8 relative" data-testid={testid}>
    <span className="absolute -left-[13px] top-0 w-6 h-6 bg-canvas border border-brand text-brand font-mono text-[10px] grid place-items-center">{n}</span>
    <p className="font-bold">{title}</p>
    <div className="mt-3">{children}</div>
  </div>
);

export const DeployWizard = ({ health, onChange }) => {
  const { isOwner, canWrite } = useAuth();
  const [pid, setPid] = useState("stripe");
  const [key, setKey] = useState(null);
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);
  const p = PLATFORMS.find((x) => x.id === pid);
  const h = health?.platforms?.find((x) => x.platform === pid);

  const pick = (id) => { setPid(id); setKey(null); setTest(null); };
  const createKey = async () => {
    try { const { data } = await api.post("/hub/keys", { name: `${p.name} integration`, platform: p.id, scopes: p.scopes }); setKey(data.key); toast.success("Key created. Copy it now; it won't be shown again."); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runTest = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/hub/test/${p.id}`); setTest(data); onChange(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-8" data-testid="deploy-wizard">
      <ul className="space-y-2">
        {PLATFORMS.map((x) => {
          const hh = health?.platforms?.find((y) => y.platform === x.id);
          return (
            <li key={x.id}><button onClick={() => pick(x.id)} data-testid={`platform-${x.id}`}
              className={`w-full flex items-center justify-between px-4 py-3 border text-left transition-colors ${pid === x.id ? "border-brand bg-brand/[0.05]" : "border-white/10 hover:border-white/30"}`}>
              <span><span className="block text-sm">{x.name}</span><span className="block text-[10px] text-white/40">{x.kind}</span></span>
              <span className="w-2 h-2 rounded-full" style={{ background: HEALTH_C[hh?.status] || "#333" }} title={hh?.status} />
            </button></li>
          );
        })}
      </ul>
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <h3 className="text-2xl font-extrabold">Deploy {p.name}</h3>
          <span className="font-mono text-[10px] px-2 py-1 border" style={{ color: HEALTH_C[h?.status], borderColor: `${HEALTH_C[h?.status]}66` }} data-testid="platform-health">
            {(h?.status || "waiting").toUpperCase()}{h?.last_event_at && ` · last event ${new Date(h.last_event_at).toLocaleString()}`}
          </span>
        </div>
        <Step n="1" title="Create a scoped API key" testid="wizard-step-key">
          <p className="text-xs text-white/50 mb-3">Scopes: {p.scopes.join(", ")}. Shown once; revoke any time in the API keys tab.</p>
          {key ? <CodeBlock title="Your API key (copy now)" code={key} testid="wizard-new-key" />
            : isOwner ? <NeonButton onClick={createKey} data-testid="wizard-create-key"><KeyRound className="w-3.5 h-3.5" /> Create {p.name} key</NeonButton>
              : <p className="text-xs text-[#FFB800]">Ask the workspace owner to create a key.</p>}
        </Step>
        <Step n="2" title="Connect it" testid="wizard-step-connect">
          <ol className="space-y-1.5 text-sm text-white/75 mb-4 list-decimal pl-4">{p.steps.map((s) => <li key={s} className="break-words">{s}</li>)}</ol>
          <div className="space-y-3">{p.snippets(key).map(([t, c], i) => <CodeBlock key={t} title={t} code={c} testid={`snippet-${p.id}-${i}`} />)}</div>
        </Step>
        <Step n="3" title="Test the connection" testid="wizard-step-test">
          {!canWrite ? <p className="text-xs text-white/40">Viewers can see health above; ask an analyst or owner to run tests.</p> : <NeonButton onClick={runTest} disabled={busy} data-testid="wizard-test-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />} Test connection</NeonButton>}
          {test && (
            <ul className="mt-4 space-y-2" data-testid="wizard-test-result">
              {test.checks.map((c) => (
                <li key={c.check} className="flex items-start gap-2 text-sm">
                  {c.ok ? <CheckCircle2 className="w-4 h-4 text-brand shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-[#F87171] shrink-0 mt-0.5" />}
                  <span>{c.check}<span className="block font-mono text-[10px] text-white/45">{c.detail}</span></span>
                </li>
              ))}
              <li className={`font-mono text-xs ${test.ok ? "text-brand" : "text-[#FFB800]"}`} data-testid="wizard-test-summary">{test.ok ? "✓ CONNECTION VERIFIED" : "SOME CHECKS NEED ATTENTION"}</li>
            </ul>
          )}
        </Step>
      </div>
    </div>
  );
};
